import { useContext, createContext, useState, useEffect, useRef } from "react";
import {
  expiresWithInXMinutes,
  isTokenExpired,
  CONSTANTS,
  KycData,
  ProfileData,
} from "@archax/web-lib";
import { getProfile } from "../../services/get-profile";
import { getKyc } from "../../services/get-kyc";

/**
 * The buffer time in minutes to substract from the expiry time of a token.
 * This keeps the token "fresh".
 */
const RENEWAL_BUFFER = 5;
const DATA_POLL_INTERVAL = 10000;

const AceAccountManagementContext = createContext<{
  profileData: any;
  kycData: KycData | null;
  loading: boolean;
  currentToken: any;
  triggerDataUpdate: () => void;
  renewToken: () => void;
}>({
  profileData: null,
  kycData: null,
  loading: false,
  currentToken: "",
  triggerDataUpdate: () => {
    // no-op
  },
  renewToken: () => {
    // no-op
  },
});

interface AceAccountManagementProviderProps {
  token: string;
  autoRenewalStorageKey?: string;
  aceAuthRestUrl: string;
  onError?: (error: string) => void;
  children: React.ReactNode;
}

/**
 * The context provider is built to be used with `useAceAccountManagement` - requires a JWT and the ace-auth url (should be passed in the applications .env)
 *
 * @param {string} props.token - The token retrieved from the ACE AUTH Rest endpoint.
 * @param {string=} props.autoRenewalStorageKey - Optional: if given a storage key, this provider will make the renewal calls to ensure that localstorage entry has a valid token.
 * @param {string} props.aceAuthRestUrl - The url for the ace auth REST endpoint.
 * @param {string} props.onError - A callback triggered when a failure occurs.
 *
 */

const AceAccountManagementProvider = (
  props: AceAccountManagementProviderProps,
) => {
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [kycData, setKycData] = useState<KycData | null>(null);
  const [token, setToken] = useState(props.token);
  const tokenRef = useRef(props.token);
  const pageVisible = useRef(true);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const fetchUserProfile = () => {
    getProfile()
      .then((data) => setProfileData(data))
      .catch((e) => {
        console.error(e);
        if (CONSTANTS.REASONS_TO_END_SESSION.includes(e.message)) {
          props.onError && props.onError(e.message);
          throw new Error(e.message);
        }
      });
  };

  const fetchUserKyc = () => {
    getKyc()
      .then((data) => setKycData(data))
      .catch((e) => {
        console.error(e);
        if (CONSTANTS.REASONS_TO_END_SESSION.includes(e.message)) {
          props.onError?.(e.message);
          throw new Error(e.message);
        }
      });
  };

  const renewToken = () => {
    fetch(`${props.aceAuthRestUrl}/renew-jwt`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${tokenRef.current}`,
      },
    })
      .then((response) => response.json())
      .then((res) => {
        if (res.status === "ERROR") {
          const error = res.error[0].msg;
          throw new Error(error);
        }
        setToken(res.data.jwt);
        if (props.autoRenewalStorageKey) {
          globalThis.sessionStorage.setItem(
            props.autoRenewalStorageKey,
            res.data.jwt,
          );
          globalThis.dispatchEvent(
            new StorageEvent("storage", {
              key: props.autoRenewalStorageKey,
              oldValue: null,
              newValue: res.data.jwt,
              url: globalThis.location.href,
              storageArea: sessionStorage,
            }),
          );
        }
      })
      .catch((e) => {
        props.onError?.(e);
      });
  };

  // These need to be in the one function cause calling for data close to or during renewing can causes JWT errors.
  const setUpDataAndRenewInterval = () => {
    fetchUserKyc();
    fetchUserProfile();

    return setInterval(() => {
      if (
        props.autoRenewalStorageKey &&
        expiresWithInXMinutes(tokenRef.current, RENEWAL_BUFFER)
      ) {
        renewToken();
        return;
      }

      if (pageVisible.current) {
        fetchUserKyc();
        fetchUserProfile();
      }
    }, DATA_POLL_INTERVAL);
  };

  const triggerDataUpdate = () => {
    setProfileData(null);
    setKycData(null);
    fetchUserKyc();
    fetchUserProfile();
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible") {
      pageVisible.current = true;
      if (isTokenExpired(tokenRef.current)) {
        props.onError?.("JWT expired");
      }
    }
    if (document.visibilityState === "hidden") {
      pageVisible.current = false;
    }
  };

  useEffect(() => {
    if (isTokenExpired(props.token)) {
      props.onError?.("JWT Expired");
    }

    const dataAndRenewHandle = setUpDataAndRenewInterval();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(dataAndRenewHandle);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AceAccountManagementContext.Provider
      value={{
        profileData,
        kycData,
        loading: !(profileData && kycData),
        currentToken: token,
        triggerDataUpdate,
        renewToken,
      }}
    >
      {props.children}
    </AceAccountManagementContext.Provider>
  );
};

/**
 *
 * This react hook maintains up-to-date profile, kyc and other information a UI would need to support Ace Account Management functionality.
 *
 * Any alterations made to profile either manually or REST calls in other parts of this app. Should quickly (long-polling vs real-time) reflect in the data provided by this hook.
 *
 * ```
 * const { profileData, kycData, loading, currentToken, triggerDataUpdate } = useAceAccountManagement();
 * ```
 *
 * To be used with `<AceAccountManagementProvider />` at the root of your application.
 *
 */
const useAceAccountManagement = () => {
  const {
    profileData,
    kycData,
    loading,
    currentToken,
    triggerDataUpdate,
    renewToken,
  } = useContext(AceAccountManagementContext);

  return {
    profileData,
    kycData,
    loading,
    currentToken,
    triggerDataUpdate,
    renewToken,
  };
};

export { AceAccountManagementProvider, useAceAccountManagement };
